/* eslint-disable @typescript-eslint/camelcase */
import {
  ConvertObjectCamelToSnakeCase,
  isUrlUpdateVersion
} from "@/app/infrastructures/misc/Utils";
import { QueryParamsEntities } from "@/domain/entities/MainApp";
import { ReferenceChipV4, ReferenceTypeV4 } from "@/domain/entities/BookingV4";

export type CIPLTypeV4 = {
  itemDetail: string;
  itemDetailEn: string;
  quantity: number;
  itemPrice: number;
  commodityHsCode: string;
};

// --- GENESIS-19590 — Submit STT. Full clone of `CreateBookingV3Retail`
// (BookingRequest.ts) — NOT `extends`, v4 payload builder stays decoupled
// from V3's class hierarchy per detailing "tidak ada perubahan pada kelas
// CreateBookingV3Retail". Adds root `booking_form_version` (sibling to
// voucher_*/otp_*, contract GENESIS-19359); everything else mirrors V3
// 1:1 (bulk_id/bulk_stt_row_number and the elexys-migration payload are
// V3-legacy concerns not applicable to the v4 manual retail flow, so
// they're intentionally left out).
export class ManualBookingV4Retail {
  sttManual = "";
  referenceExternal = "";
  productName = "";
  originCity = "";
  originDistrict = "";
  destinationCity = "";
  destinationDistrict = "";
  phoneSender = "";
  phoneReceiver = "";
  sender = "";
  receiver = "";
  addressSender = "";
  addressReceiver = "";
  addressReceiverType = "";
  sttGrossWeight = 0;
  sttVolumeWeight = 0;
  sttChargeableWeight = 0;
  commodityCode = "";
  insurance = "";
  statusGoods = "";
  estimationPrice = 0;
  taxNumber = "";
  isWoodpack = false;
  isCOD = false;
  isDFOD = false;
  sttAttachFiles: string[] = [];
  sttCommodityDetail = "";
  isSaveSender = false;
  isSaveReceiver = false;
  sttPieces: any[] = [];
  isDO = false;
  isMixpack = false;
  sttPiecePerPack = 0;
  sttNextCommodity = "";
  sttCodAmount = 0;
  postalCodeDestination = "";
  isPAD = false;
  sttWeightAttachFiles: string[] = [];
  sttRecipientEmail = "";
  sttKtpImage = "";
  sttTaxImage = "";
  goodsNames: string[] = [];
  sttIdentityNumber = "";
  sttInterTaxNumber = "";
  sttShipmentId = "";
  sttCipl: CIPLTypeV4[] = [];
  sttFtzCipl: CIPLTypeV4[] = [];
  sttFtzRecipientEmail = "";
  sttFtzIdentityNumber = "";
  sttFtzTaxImage = "";
  sttFtzKtpImage = "";
  sttFtzAttachFiles: any[] = [];
  saveCustomerSender = false;
  saveCustomerRecipient = false;
  sttPaymentMethod = "";
  sttDutyTaxTerms = "";
  voucherId = "";
  voucherCodeId = 0;
  voucherCode = "";
  goberReservationId = 0;
  otpToken = "";
  otpCode = "";
  bookingFormVersion = "";
  savePackage = false;
  savedPackage: { commodity_code: string; pieces: { gross_weight: number; length: number; width: number; height: number }[] } | null = null;
  activeSavedPackageId: number | null = null;
  is_selected_saved_package_without_id = false;

  constructor(fields?: Partial<ManualBookingV4Retail>) {
    Object.assign(this, fields);
  }

  // Mirrors V3's dedicated STT Manual / reference-eksternal fields, but
  // sourced from v4's `ReferenceChipV4[]` chips (GENESIS-19949). Retail
  // never produces a `shipment_id` chip (the `CP` prefix stays classified
  // as `external_ref` in the form), so it's intentionally not read here.
  static fromReferenceChips(
    chips: ReferenceChipV4[] = []
  ): Pick<ManualBookingV4Retail, "sttManual" | "referenceExternal"> {
    const sttManualChip = chips.find(
      (chip) => chip.type === ReferenceTypeV4.STT_MANUAL
    );
    const externalRefChip = chips.find(
      (chip) => chip.type === ReferenceTypeV4.EXTERNAL_REF
    );
    return {
      sttManual: sttManualChip?.value.trim() || "",
      referenceExternal: externalRefChip?.value.trim() || ""
    };
  }

  payload1() {
    return {
      stt_no: this.sttManual,
      is_mixpack: this.isMixpack,
      stt_shipment_id: this.sttShipmentId,
      stt_no_ref_external: this.referenceExternal,
      stt_tax_number: this.taxNumber,
      stt_goods_estimate_price: this.estimationPrice,
      stt_goods_status: this.statusGoods,
      stt_origin_city_id: this.originCity,
      stt_origin_district_id: this.originDistrict,
      stt_destination_city_id: this.destinationCity,
      stt_destination_district_id: this.destinationDistrict,
      stt_sender_name: this.sender,
      stt_sender_phone: this.phoneSender,
      stt_sender_address: this.addressSender,
      stt_is_save_sender: this.isSaveSender,
      stt_recipient_name: this.receiver,
      stt_recipient_address: this.addressReceiver,
      stt_recipient_address_type: this.addressReceiverType,
      stt_recipient_phone: this.phoneReceiver,
      stt_is_save_recipient: this.isSaveReceiver,
      stt_insurance_type: this.insurance,
      stt_product_type: this.productName,
      stt_gross_weight: this.sttGrossWeight,
      stt_volume_weight: this.sttVolumeWeight,
      stt_chargeable_weight: this.sttChargeableWeight,
      stt_commodity_code: this.commodityCode
    };
  }

  payload2() {
    return {
      stt_client_stt_id: 0,
      stt_vendor_stt_id: 0,
      stt_is_cod: this.isCOD,
      stt_is_dfod: this.isDFOD,
      stt_attach_files: this.sttAttachFiles,
      stt_commodity_detail: this.sttCommodityDetail,
      stt_is_do: this.isDO,
      stt_is_woodpacking: this.isWoodpack,
      stt_pieces: this.sttPieces,
      stt_piece_per_pack: this.sttPiecePerPack,
      stt_next_commodity: this.sttNextCommodity,
      postal_code_destination: this.postalCodeDestination,
      is_pad: this.isPAD,
      stt_recipient_email: this.sttRecipientEmail,
      stt_ktp_image: this.sttKtpImage,
      stt_tax_image: this.sttTaxImage,
      stt_identity_number: this.sttIdentityNumber,
      stt_inter_tax_number: this.sttInterTaxNumber
    };
  }

  payload3() {
    return {
      stt_cipl: this.sttCipl.map((cipl) => ({
        item_detail: cipl.itemDetail,
        item_detail_en: cipl.itemDetailEn,
        quantity: cipl.quantity,
        item_price: cipl.itemPrice,
        commodity_hs_code: cipl.commodityHsCode
      })),
      stt_ftz_cipl: this.sttFtzCipl.map((cipl) => ({
        item_detail: cipl.itemDetail,
        item_detail_en: cipl.itemDetailEn,
        quantity: cipl.quantity,
        item_price: cipl.itemPrice,
        commodity_hs_code: cipl.commodityHsCode
      })),
      stt_ftz_recipient_email: this.sttFtzRecipientEmail,
      stt_ftz_identity_number: this.sttFtzIdentityNumber,
      stt_ftz_tax_image: this.sttFtzTaxImage,
      stt_ftz_ktp_image: this.sttFtzKtpImage,
      stt_ftz_attach_files: this.sttFtzAttachFiles,
      is_new_form: true,
      stt_payment_method: this.sttPaymentMethod,
      stt_duty_tax_terms: this.sttDutyTaxTerms
    };
  }

  toJSON(): string {
    const objectPayload = {
      stt: {
        ...this.payload1(),
        ...this.payload2(),
        ...this.payload3()
      },
      stt_weight_attach_files: this.sttWeightAttachFiles,
      goods_names: this.goodsNames,
      save_customer_sender: this.saveCustomerSender,
      save_customer_recipient: this.saveCustomerRecipient,
      voucher_id: this.voucherId || undefined,
      voucher_code_id: this.voucherCodeId || undefined,
      voucher_code: this.voucherCode || undefined,
      gober_reservation_id: this.goberReservationId || undefined,
      otp_token: this.otpToken || undefined,
      otp_code: this.otpCode || undefined,
      booking_form_version: this.bookingFormVersion || undefined,
      save_package: this.savePackage || undefined,
      saved_package: this.savedPackage || undefined,
      active_saved_package_id: this.activeSavedPackageId || undefined,
      is_selected_saved_package_without_id: this.is_selected_saved_package_without_id || undefined
    } as any;
    if (this.isCOD) {
      Object.assign(objectPayload.stt, {
        stt_cod_amount: this.sttCodAmount
      });
    }
    if (this.isDFOD) {
      Object.assign(objectPayload.stt, {
        stt_cod_amount: 0
      });
    }
    return JSON.stringify(objectPayload);
  }
}
export class CorporateBookingV4Client {
  sttClient = 0;
  sttManual = "";
  referenceExternal = "";
  productName = "";
  originCity = "";
  originDistrict = "";
  destinationCity = "";
  destinationDistrict = "";
  phoneSender = "";
  phoneReceiver = "";
  sender = "";
  receiver = "";
  addressSender = "";
  addressReceiver = "";
  addressReceiverType = "";
  sttGrossWeight = 0;
  sttVolumeWeight = 0;
  sttChargeableWeight = 0;
  commodityCode = "";
  insurance = "";
  statusGoods = "";
  estimationPrice = 0;
  taxNumber = "";
  isWoodpack = false;
  isCOD = false;
  isDFOD = false;
  sttAttachFiles: string[] = [];
  sttCommodityDetail = "";
  isSaveSender = false;
  isSaveReceiver = false;
  sttPieces: any[] = [];
  isDO = false;
  isMixpack = false;
  sttPiecePerPack = 0;
  sttNextCommodity = "";
  sttCodAmount = 0;
  postalCodeDestination = "";
  isPAD = false;
  sttWeightAttachFiles: string[] = [];
  sttRecipientEmail = "";
  sttKtpImage = "";
  sttTaxImage = "";
  goodsNames: string[] = [];
  sttIdentityNumber = "";
  sttInterTaxNumber = "";
  sttShipmentId = "";
  sttCipl: CIPLTypeV4[] = [];
  sttFtzCipl: CIPLTypeV4[] = [];
  sttFtzRecipientEmail = "";
  sttFtzIdentityNumber = "";
  sttFtzTaxImage = "";
  sttFtzKtpImage = "";
  sttFtzAttachFiles: any[] = [];
  saveCustomerSender = false;
  saveCustomerRecipient = false;
  sttPaymentMethod = "";
  sttDutyTaxTerms = "";
  bookingFormVersion = "";
  savePackage = false;
  savedPackage: { commodity_code: string; pieces: { gross_weight: number; length: number; width: number; height: number }[] } | null = null;
  activeSavedPackageId: number | null = null;
  is_selected_saved_package_without_id = false;

  constructor(fields?: Partial<CorporateBookingV4Client>) {
    Object.assign(this, fields);
  }

  // Mirrors V3's dedicated STT Manual / reference-eksternal fields, but
  // sourced from v4's `ReferenceChipV4[]` chips (GENESIS-19949). Retail
  // never produces a `shipment_id` chip (the `CP` prefix stays classified
  // as `external_ref` in the form), so it's intentionally not read here.
  static fromReferenceChips(
    chips: ReferenceChipV4[] = []
  ): Pick<ManualBookingV4Retail, "sttManual" | "referenceExternal" | "sttShipmentId"> {
    const sttManualChip = chips.find(
      (chip) => chip.type === ReferenceTypeV4.STT_MANUAL
    );
    const externalRefChip = chips.find(
      (chip) => chip.type === ReferenceTypeV4.EXTERNAL_REF
    );
    const shipmentIdChip = chips.find(
      (chip) => chip.type === ReferenceTypeV4.SHIPMENT_ID
    )
  
    return {
      sttManual: sttManualChip?.value.trim() || "",
      referenceExternal: externalRefChip?.value.trim() || "",
      sttShipmentId: shipmentIdChip?.value.trim() || ""
    };
  }

  payload1() {
    return {
      stt_no: this.sttManual,
      is_mixpack: this.isMixpack,
      stt_shipment_id: this.sttShipmentId,
      stt_no_ref_external: this.referenceExternal,
      stt_tax_number: this.taxNumber,
      stt_goods_estimate_price: this.estimationPrice,
      stt_goods_status: this.statusGoods,
      stt_origin_city_id: this.originCity,
      stt_origin_district_id: this.originDistrict,
      stt_destination_city_id: this.destinationCity,
      stt_destination_district_id: this.destinationDistrict,
      stt_sender_name: this.sender,
      stt_sender_phone: this.phoneSender,
      stt_sender_address: this.addressSender,
      stt_is_save_sender: this.isSaveSender,
      stt_recipient_name: this.receiver,
      stt_recipient_address: this.addressReceiver,
      stt_recipient_address_type: this.addressReceiverType,
      stt_recipient_phone: this.phoneReceiver,
      stt_is_save_recipient: this.isSaveReceiver,
      stt_insurance_type: this.insurance,
      stt_product_type: this.productName,
      stt_gross_weight: this.sttGrossWeight,
      stt_volume_weight: this.sttVolumeWeight,
      stt_chargeable_weight: this.sttChargeableWeight,
      stt_commodity_code: this.commodityCode
    };
  }

  payload2() {
    return {
      stt_client_stt_id: 0,
      stt_vendor_stt_id: 0,
      stt_is_cod: this.isCOD,
      stt_is_dfod: this.isDFOD,
      stt_attach_files: this.sttAttachFiles,
      stt_commodity_detail: this.sttCommodityDetail,
      stt_is_do: this.isDO,
      stt_is_woodpacking: this.isWoodpack,
      stt_pieces: this.sttPieces,
      stt_piece_per_pack: this.sttPiecePerPack,
      stt_next_commodity: this.sttNextCommodity,
      postal_code_destination: this.postalCodeDestination,
      is_pad: this.isPAD,
      stt_recipient_email: this.sttRecipientEmail,
      stt_ktp_image: this.sttKtpImage,
      stt_tax_image: this.sttTaxImage,
      stt_identity_number: this.sttIdentityNumber,
      stt_inter_tax_number: this.sttInterTaxNumber
    };
  }

  payload3() {
    return {
      stt_cipl: this.sttCipl.map((cipl) => ({
        item_detail: cipl.itemDetail,
        item_detail_en: cipl.itemDetailEn,
        quantity: cipl.quantity,
        item_price: cipl.itemPrice,
        commodity_hs_code: cipl.commodityHsCode
      })),
      stt_ftz_cipl: this.sttFtzCipl.map((cipl) => ({
        item_detail: cipl.itemDetail,
        item_detail_en: cipl.itemDetailEn,
        quantity: cipl.quantity,
        item_price: cipl.itemPrice,
        commodity_hs_code: cipl.commodityHsCode
      })),
      stt_ftz_recipient_email: this.sttFtzRecipientEmail,
      stt_ftz_identity_number: this.sttFtzIdentityNumber,
      stt_ftz_tax_image: this.sttFtzTaxImage,
      stt_ftz_ktp_image: this.sttFtzKtpImage,
      stt_ftz_attach_files: this.sttFtzAttachFiles,
      is_new_form: true,
      stt_payment_method: this.sttPaymentMethod,
      stt_duty_tax_terms: this.sttDutyTaxTerms
    };
  }

  toJSON(): string {
    const objectPayload = {
      stt_client: this.sttClient,
      stt: {
        ...this.payload1(),
        ...this.payload2(),
        ...this.payload3()
      },
      stt_weight_attach_files: this.sttWeightAttachFiles,
      goods_names: this.goodsNames,
      save_customer_sender: this.saveCustomerSender,
      save_customer_recipient: this.saveCustomerRecipient,
      booking_form_version: this.bookingFormVersion || undefined,
      save_package: this.savePackage || undefined,
      saved_package: this.savedPackage || undefined,
      active_saved_package_id: this.activeSavedPackageId || undefined,
      is_selected_saved_package_without_id: this.is_selected_saved_package_without_id || undefined
    } as any;
    if (this.isCOD) {
      Object.assign(objectPayload.stt, {
        stt_cod_amount: this.sttCodAmount
      });
    }
    if (this.isDFOD) {
      Object.assign(objectPayload.stt, {
        stt_cod_amount: 0
      });
    }
    return JSON.stringify(objectPayload);
  }
}

// --- GENESIS-19591 — Tariff estimation (skeleton; mapping bookingFormV4 -> payload di subtask API) ---
export class GetEstimationPriceV4 {
  originId = "";
  destinationId = "";
  commodityId = 0;
  productType = "";
  accountType = "";
  accountRefId = 0;
  goodsPrice = 0;
  insuranceType = "";
  isWoodpacking = false;
  isHaveTaxId = false;
  pieces: any[] = [];
  isCod = false;
  isDfod = false;
  codHandling = "";
  codAmount = 0;
  isDisablePromo = false;
  promoAppliedTo = "";
  isPad = false;
  shipmentPrefix = "";
  hideError = false;
  discountFavoritePercentage = 0;
  sttBookedAt = "";
  shipmentCreatedAt = "";
  senderPhone = "";
  voucherCode = "";
  sttNo = "";

  constructor(fields?: Partial<GetEstimationPriceV4>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify(ConvertObjectCamelToSnakeCase(this));
  }
}

// --- GENESIS-19592 — Voucher / OTP ---
export class GetBookingVoucherListV4 {
  originId = "";
  destinationId = "";
  commodityId = 0;
  productType = "";
  accountType = "";
  accountRefId = 0;
  goodsPrice = 0;
  insuranceType = "";
  isWoodpacking = false;
  isHaveTaxId = false;
  pieces: any[] = [];
  isCod = false;
  isDfod = false;
  codHandling = "";
  codAmount = 0;
  isDisablePromo = false;
  promoAppliedTo = "";
  isPad = false;
  shipmentPrefix = "";
  hideError = false;
  senderPhone = "";
  cursor = "";
  limit = 20;

  constructor(fields?: Partial<GetBookingVoucherListV4>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify(ConvertObjectCamelToSnakeCase(this));
  }
}

export class ValidateBookingVoucherCodeV4 {
  voucherCode = "";
  originId = "";
  destinationId = "";
  commodityId = 0;
  productType = "";
  accountType = "";
  accountRefId = 0;
  goodsPrice = 0;
  insuranceType = "";
  isWoodpacking = false;
  isHaveTaxId = false;
  pieces: any[] = [];
  isCod = false;
  isDfod = false;
  codHandling = "";
  codAmount = 0;
  isDisablePromo = false;
  promoAppliedTo = "";
  isPad = false;
  shipmentPrefix = "";
  hideError = false;
  senderPhone = "";

  constructor(fields?: Partial<ValidateBookingVoucherCodeV4>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify(ConvertObjectCamelToSnakeCase(this));
  }
}

export class IssueVoucherBookingOtpRequestV4 {
  voucherCode = "";
  senderPhone = "";
  bookingAttemptId = "";
  voucherId = "";
  voucherCodeId = 0;

  constructor(fields?: Partial<IssueVoucherBookingOtpRequestV4>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify(ConvertObjectCamelToSnakeCase(this));
  }
}

export class CancelVoucherBookingReserveRequestV4 {
  voucherCode = "";
  senderPhone = "";
  otpToken = "";
  goberReservationId = 0;

  constructor(fields?: Partial<CancelVoucherBookingReserveRequestV4>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify(ConvertObjectCamelToSnakeCase(this));
  }
}

// --- GENESIS-19593 — Product / rule / commodity ---
export class ProductConfigurationRuleRequestParamsV4 {
  page = 1;
  limit = 10;
  isNoLimit = true;
  productType = "";
  countryCode = "";

  constructor(fields?: Partial<ProductConfigurationRuleRequestParamsV4>) {
    Object.assign(this, fields);
  }

  get toQueryString(): string {
    return new QueryParamsEntities(this).queryString;
  }
}

export class RequestListCommodityV4 {
  cache = true;
  search = "";
  commodityStatus = "";
  page = 1;
  limit = 10;
  version = isUrlUpdateVersion("commodity");
  isTotalData = false;
  isDisplay = false;
  isRecommendedOnly = false;
  commodityCategoryCode = "";
  countryCode = "";

  constructor(fields?: Partial<RequestListCommodityV4>) {
    Object.assign(this, fields);
  }

  toQueryString(): string {
    return new QueryParamsEntities(this).queryString;
  }
}

// --- GENESIS-19589 — Exchange rate (Est. CIPL) ---
export interface ExchangeRateParamsV4 {
  from: string; // ISO currency code
  to: string; // ISO currency code
}
